import { Scene, Math as MATH } from "phaser";
import common from "./common";
import { resolveTime } from "../common/utils";
import CONSTANTS from "./constants";
import GameService from "../../../api/services/GameService";
import EventTracker from "../../../utils/EventTracker";

const scalePhaser = (scene) => {
  const w = window.innerWidth;
  const h = window.innerHeight;

  const defaultWidth = +scene.sys.game.config.width;
  const defaultHeight = +scene.sys.game.config.height;

  const scale = Math.min(w / defaultWidth, h / defaultHeight);

  const width = defaultWidth * scale;
  const height = defaultHeight * scale;

  scene.game.canvas.style.width = width + "px";
  scene.game.canvas.style.height = height + "px";

  scene.game.canvas.style.marginTop = `${(h - height) / 2}px`;
  scene.game.canvas.style.marginLeft = `${(w - width) / 2}px`;

  scene.game.scale.displaySize.setWidth(width);
  scene.game.scale.displaySize.setHeight(height);
};

class MainScene extends Scene {
  constructor() {
    super({ key: "MAIN" });
  }

  init({
    step,
    data,
    sheet = null,
    list = [],
    inputs = null,
    history = null,
    exit = null,
  }) {
    this.overlay = {};
    ["fly", "face"].map((key) => this.anims.remove(key));
    this.history = history ? history : this.history;
    this.data = data;
    this.step = step;
    this.camera = this.cameras.main;
    this.playerSheet = sheet ? sheet : this.playerSheet;
    this.recommendations =
      list && list.length > 0 ? list : this.recommendations || list;
    this.inputs =
      this.inputs && this.inputs.textarea && this.inputs.textarea.length > 0
        ? this.inputs
        : inputs
        ? inputs
        : this.data.response &&
          this.data.response.input &&
          this.data.response.input.length > 0
        ? { textarea: this.data.response.input }
        : { textarea: [] };
    this.inputs.textarea = this.inputs.textarea
      .filter((v, i, s) => s.indexOf(v) === i)
      .filter((v) => v.length);
    this.exit = exit ? exit : this.exit;
    let { _id, submitQuery } = this.data.game.game.screens[this.step];
    this.screenID = step > 0 ? _id : null;
    if (submitQuery) {
      (async () => {
        let payload = { screen: _id, sheet };
        if (
          this.inputs &&
          this.inputs.textarea &&
          this.inputs.textarea.length > 0
        )
          payload.input = this.inputs.textarea
            .filter((v, i, s) => s.indexOf(v) === i)
            .filter((v) => v.length);
        if (this.score && this.score > -1) payload.score = this.score;
        if (this.mood) payload.mood = this.mood;
        if (this.gameStart) payload.gameStart = this.gameStart;
        if (this.startTime) payload.startTime = this.startTime;
        payload.endTime = Date.now();
        await GameService.dynamic(submitQuery, JSON.stringify(payload));
      })();
    }
    this.startTime = Date.now();
    if (!this.activities) {
      this.camera.fadeIn(600);
    }
    this.scale.on("resize", () => {
      let body = document.getElementById("phaser");
      if (!body) return;
      if (document.activeElement && document.activeElement.tagName !== "BODY") {
        body.style.overflowY = "auto";
      } else {
        if (body) body.style.overflowY = "hidden";
        scalePhaser(this);
      }
    });
  }

  preload() {
    let bar = this.add.graphics(),
      box = this.add.graphics(),
      { width, height } = this.camera,
      boxWidth = 320,
      boxHeight = 50,
      barWidth = 300,
      barHeight = 30;
    box.fillStyle(0x222222, 0.8);
    box.fillRect(
      (width - boxWidth) / 2,
      (height - boxHeight) / 2,
      boxWidth,
      boxHeight
    );
    let loadingText = this.make.text({
        x: width / 2,
        y: height / 2 - boxHeight,
        text: "Loading...",
        style: {
          font: "20px A-AlloyInk",
          fill: "#ffffff",
        },
      }),
      percentText = this.make.text({
        x: width / 2,
        y: height / 2,
        text: "0%",
        style: {
          font: "18px monospace",
          fill: "#ffffff",
        },
      });
    loadingText.setOrigin(0.5, 0.5);
    percentText.setOrigin(0.5, 0.5);
    this.load.on("progress", (value) => {
      percentText.setText(`${parseInt(value * 100)}%`);
      bar.clear();
      bar.fillStyle(0xffffff, 1);
      bar.fillRect(
        (width - barWidth) / 2,
        (height - barHeight) / 2,
        barWidth * value,
        barHeight
      );
    });
    this.load.on("complete", () => {
      bar.destroy();
      box.destroy();
      loadingText.destroy();
      percentText.destroy();
    });
    const game = this,
      assets = this.data.game.game.assets,
      assetKind = width > 800 && height > 380 ? 1 : 0.75,
      resolveAsset = (item, kind) => {
        let { type, resource } = assets[kind][item];
        if (type === "IMAGE") game.load.image(item, resource);
        else if (type === "SPRITESHEET")
          game.load.spritesheet(item, resource, {
            frameWidth: assetKind === 1 ? 162 : 121,
            frameHeight: assetKind === 1 ? 199 : 149,
          });
        else if (type === "AUDIO") game.load.audio("bgm", [resource]);
        return null;
      };
    Object.keys(assets.common).map((item) => resolveAsset(item, "common"));
    Object.keys(assets[assetKind]).map((item) => resolveAsset(item, assetKind));
  }

  create() {
    let { spritesheet = {}, screens = [] } = this.data.game.game,
      { width: cameraWidth, height: cameraHeight } = this.camera,
      {
        name,
        screen,
        kind,
        values = null,
        placeholders = [],
        background,
        layers = [],
        createQuery,
        fetchQuery,
        coordinates = {},
      } = screens[this.step],
      scene = this,
      spriteMap = {},
      layerList = [];
    scene.screen_name = screen;
    scene.game_name = scene.data.game.slug;
    scene.spriteList = [];
    if (!scene.bgm) {
      scene.bgm = scene.sound.add("bgm", { loop: true });
      scene.bgm.play();
    }
    if (background) this.camera.setBackgroundColor(background);
    layers.map((layer) => {
      let {
          alpha = null,
          html,
          sprites = [],
          labels = [],
          players = [],
          dialogue = null,
        } = layer,
        spriteList = [];
      sprites.map((sprite) => {
        let {
            sprite: key,
            position,
            scale,
            listener = null,
            hide = false,
            input,
          } = sprite,
          spriteObject =
            input != null
              ? common.addSpriteWithText(
                  scene,
                  key,
                  position,
                  scale,
                  scene.inputs.textarea[input]
                )
              : common.addSprite(scene, key, position, scale);
        if (listener) {
          spriteObject = spriteObject.setInteractive();
          spriteObject.on("pointerdown", () => {
            EventTracker.instance.trackEvent("Game Button Click", {
              "CTA-Name": key,
              "Screen-Name": scene.screen_name,
            });
            if (["sunny", "gloomy"].includes(key)) {
              this.mood = key.toUpperCase();
            }
            let { action } = listener;
            if (
              ["TOGGLE", "TRAVEL"].includes(action) &&
              scene.screen_name === "CHARACTER_CUSTOMISATION"
            ) {
              if (action === "TOGGLE")
                EventTracker.instance.trackEvent(
                  "Start Character Customization"
                );
              else if (action === "TRAVEL")
                EventTracker.instance.trackEvent(
                  `${
                    key === "skip" ? "Skip" : "Complete"
                  } Character Customization`
                );
            }
            if (action === "TOGGLE") {
              let { show = [], hide = [] } = listener,
                i;
              for (i = 0; i < show.length; i++)
                spriteMap[show[i]].setVisible(true);
              for (i = 0; i < hide.length; i++)
                spriteMap[hide[i]].setVisible(false);
            } else {
              let { action } = listener;
              if (action === "SESSION") {
                EventTracker.instance.trackEvent("Start Game", {
                  "Game-Name": scene.game_name,
                  "User-Type":
                    scene.data.recommendations.length > 0
                      ? "Old User"
                      : "New User",
                });
                (async () => {
                  const { success, data } = await GameService.dynamic(
                    createQuery,
                    {}
                  );
                  if (success) {
                    common.resolveAction(
                      scene,
                      {
                        ...listener,
                        action: "TRAVEL",
                      },
                      key
                    );
                    scene.startTime = Date.now();
                    scene.inputs = { textarea: [] };
                    scene.data = {
                      ...scene.data,
                      ref: data.ref,
                      done: false,
                      step: scene.step,
                      game: data.game,
                      response: {
                        ...scene.data.response,
                        input: [],
                      },
                    };
                    scene.scene.start("MAIN", {
                      step: scene.step,
                      data: scene.data,
                      sheet: scene.playerSheet,
                    });
                  }
                })();
              } else if (action === "RESET") {
                EventTracker.instance.trackEvent("Close Game", {
                  "Game-Name": scene.game_name,
                  Screen: scene.screen_name,
                });
                scene.rainDropGameScene = null;
                scene.recommendationScreen = null;
                scene.pastScreen = null;
                (async () => {
                  const { success, data } = await GameService.getGameSession(
                    this.data.game.slug
                  );
                  if (success) {
                    common.resolveAction(scene, listener, key);
                    scene.data = {
                      ...data,
                      game: data.game,
                    };
                    scene.scene.start("MAIN", {
                      step: scene.step,
                      data: scene.data,
                      sheet: scene.playerSheet,
                      list: data.recommendations || [],
                      inputs: {
                        textarea: data.response
                          ? data.response.input || []
                          : [],
                      },
                    });
                  }
                })();
              } else if (action === "TRAVEL" && fetchQuery) {
                (async () => {
                  const { success, data } = await GameService.dynamic(
                    fetchQuery,
                    {}
                  );
                  if (success) {
                    common.resolveAction(scene, listener, key);
                    scene.data = {
                      ...scene.data,
                      step: scene.step,
                    };
                    scene.scene.start("MAIN", {
                      step: scene.step,
                      data: scene.data,
                      sheet: scene.playerSheet,
                      list: data,
                    });
                  }
                })();
              } else if (action === "INFO_OVERLAY") {
                let value = listener.value || "";
                if (
                  (value && value.length > 0) ||
                  (input != null &&
                    scene.inputs &&
                    scene.inputs.textarea &&
                    scene.inputs.textarea[input] &&
                    scene.inputs.textarea[input].length > 0)
                )
                  common.addInfoOverlay(
                    scene,
                    "info",
                    input != null ? scene.inputs.textarea[input] : value
                  );
              } else if (action === "EXIT") {
                scene.rainDropGameScene = null;
                scene.recommendationScreen = null;
                scene.pastScreen = null;
                scene.bgm.pause();
                this.exit();
              } else if (action === "TOGGLE_BGM") {
                if (scene.bgm.isPlaying) {
                  spriteMap.sound.setVisible(false);
                  spriteMap.mute.setVisible(true);
                  scene.bgm.pause();
                } else if (scene.bgm.isPaused) {
                  spriteMap.sound.setVisible(true);
                  spriteMap.mute.setVisible(false);
                  scene.bgm.play();
                }
              } else {
                if (listener.action === "RESUME") {
                  scene.startTime = Date.now();
                  EventTracker.instance.trackEvent("Resume Game", {
                    "Game-Name": scene.game_name,
                  });
                }
                common.resolveAction(scene, listener, key);
                scene.scene.start("MAIN", {
                  step: scene.step,
                  data: scene.data,
                  sheet: scene.playerSheet,
                });
              }
            }
          });
        }
        if (hide) spriteObject.setVisible(false);
        spriteMap[key] = spriteObject;
        spriteList.push(spriteObject);
        if (name === "GAME" && kind === "RAINDROP" && key.includes("dark"))
          scene.spriteList.push(spriteObject);
        return null;
      });
      labels.map((label) => {
        let {
            kind,
            seconds = 0,
            text = "",
            color,
            size,
            position,
            end,
            font,
            bold = false,
          } = label,
          container = common.addLabel(
            scene,
            position,
            size,
            kind === "TIMER" ? `${resolveTime(seconds)}` : text,
            color,
            font,
            bold
          ),
          labelObject = container.list[0];
        if (kind === "TIMER") {
          scene.time.addEvent({
            callback: () => {
              seconds -= 1;
              labelObject.text = resolveTime(seconds);
              if (seconds === 0) {
                common.resolveAction(scene, end);
                scene.gamePlayerObject.destroy();
                scene.gamePlayerObject = null;
                scene.scene.start("MAIN", {
                  step: scene.step,
                  data: scene.data,
                  sheet: scene.playerSheet,
                });
              }
            },
            repeat: seconds - 1,
            delay: 1000,
          });
        }
        return null;
      });
      players.map((player) => {
        let {
            init,
            position,
            scale,
            frames = [],
            end,
            rotate = false,
          } = player,
          sheet = scene.playerSheet || player.sheet,
          playerSheet = spritesheet[sheet.split("_")[0]],
          playerObject = common.addSprite(
            scene,
            sheet,
            position,
            scale,
            playerSheet[init]
          );
        if (rotate) playerObject.setFlipX(true);
        if (frames.length) {
          scene.playerAnimation = {
            list: [],
            running: 0,
            end,
          };
          frames.map((frame, frameIndex) => {
            let { animation, duration, repeat } = frame,
              frameKey = playerSheet[frame.frame],
              key = scene.playerSheet || frame.key;
            scene.anims.create({
              key: animation,
              frames: [{ key, frame: frameKey }],
              duration,
              repeat,
            });
            scene.playerAnimation.list.push(animation);
            if (frameIndex === 0) playerObject.anims.play(animation);
            return null;
          });
          scene.playerObject = playerObject;
        } else scene.playerObject = null;
        if (name === "GAME") {
          scene.gamePlayerObject = playerObject;
          scene.gamePlayerObject.setInteractive();
          scene.score = 0;
          scene.input.setDraggable(scene.gamePlayerObject);
          scene.gamePlayerObject.on("pointerover", () => {
            let scoreValue = parseInt(scene.score / 3),
              frameName = spritesheet.player[`bucket${scoreValue}_fly`];
            scene.gamePlayerObject.setTexture(sheet, frameName);
          });
          scene.gamePlayerObject.on("pointerout", () => {
            let scoreValue = parseInt(scene.score / 3),
              frameName = spritesheet.player[`bucket${scoreValue}_idle`];
            scene.gamePlayerObject.setTexture(sheet, frameName);
          });
          scene.input.on("drag", (pointer, gameObject, dragX, dragY) => {
            let width = scene.camera.width;
            if (gameObject.x > dragX && gameObject.flipX)
              gameObject.setFlipX(false);
            if (gameObject.x < dragX && !gameObject.flipX)
              gameObject.setFlipX(true);
            if (dragX > 0.2 * width && dragX < 0.7 * width)
              gameObject.x = dragX;
          });
        } else if (name === "GAME_INTRO") {
          let pointer = scene.add.sprite(
            playerObject.x - 50,
            playerObject.y,
            "hand"
          );
          spriteList.push(pointer);
          scene.gameIntro = {
            pointer,
            player: playerObject,
          };
        }
        spriteList.push(playerObject);
        spriteMap.player = playerObject;
        return null;
      });
      let layerObject = scene.add.layer();
      layerObject.add(spriteList);
      if (dialogue) {
        let container = common.addDialogue(scene, spriteMap, dialogue);
        layerObject.add(container);
      }
      if (html) {
        let element;
        if (html === "TEXTAREA") {
          element = scene.add
            .dom(cameraWidth / 2, cameraHeight * 0.6)
            .createFromHTML(
              CONSTANTS.HTML.TEXTAREA(
                cameraWidth,
                cameraHeight,
                placeholders.length > 0
                  ? placeholders[0]
                  : "Time to write our worries here, one cloud at a time! What's weighing you down today?"
              )
            );
          let textarea = element.getChildByID("textarea"),
            addMore = element.getChildByID("add_more"),
            back = element.getChildByID("back"),
            next = element.getChildByID("next"),
            index =
              scene.inputs &&
              scene.inputs.textarea &&
              scene.inputs.textarea.length > 0
                ? scene.inputs.textarea.length - 1
                : 0,
            max = 5,
            inputList =
              scene.inputs &&
              scene.inputs.textarea &&
              scene.inputs.textarea.length > 0
                ? scene.inputs.textarea
                : [""],
            textareaPlaceholders =
              placeholders.length > 0
                ? placeholders
                : Array.from({ length: max }).map(
                    (_) =>
                      "Time to write our worries here, one cloud at a time! What's weighing you down today?"
                  );
          if (inputList && inputList[index]) textarea.value = inputList[index];
          if (index > 0) back.hidden = false;
          element.addListener("click");
          element.on("click", (event) => {
            let id = event.target.id,
              value = textarea.value,
              nextScene = () => {
                common.resolveAction(
                  scene,
                  { action: "TRAVEL", value: 1 },
                  "done"
                );
                scene.scene.start("MAIN", {
                  step: scene.step,
                  data: scene.data,
                  sheet: scene.playerSheet,
                  input: scene.input,
                });
              };
            if (id === "add_more") {
              if (value.length > 0 && inputList.length < max) {
                inputList.push("");
                inputList[index] = value;
                index++;
                textarea.placeholder = textareaPlaceholders[index];
                back.hidden = false;
              } else if (inputList.length === max) nextScene();
            } else if (id === "done") {
              inputList[index] = value;
              scene.inputs.textarea = inputList
                .filter((v) => v.length)
                .filter((v, i, s) => s.indexOf(v) === i);
              nextScene();
            } else if (id === "back") {
              inputList[index] = textarea.value;
              index--;
              if (index === 0) back.hidden = true;
              next.hidden = false;
            } else if (id === "next") {
              inputList[index] = textarea.value;
              index++;
              if (index === inputList.length - 1 || index === max - 1)
                next.hidden = true;
              back.hidden = false;
            }
            textarea.value = inputList[index];
            if (inputList.length === max) addMore.hidden = true;
          });
        }
        layerObject.add(element);
      }
      if (alpha) layerObject.setAlpha(alpha);
      layerList.push(layerObject);
      let { width, height } = scene.camera,
        dropList = [];
      scene.spriteMap = spriteMap;
      if (layer.name === "GAME") {
        switch (layer.kind) {
          case "RAINDROP":
            if (!scene.rainDropGameScene) {
              EventTracker.instance.trackEvent("Start Raindrop Game", {
                "User-Feeling": scene.screen_name.split("_")[0],
              });
              let loading = 4,
                loadSeconds = 0,
                loadingText = scene.add
                  .text(width / 2, height / 2, "", {
                    fontFamily: "A-AlloyInk",
                    color: "#000000",
                  })
                  .setFontSize(30)
                  .setOrigin(0.5, 0.5),
                loadingBackdrop = common.addSprite(scene, "backdrop", {
                  x: "C",
                  y: "C",
                }),
                loadingLayer = scene.add.layer();
              loadingLayer.add([loadingBackdrop, loadingText]);
              scene.time.addEvent({
                callback: () => {
                  if (loadSeconds < loading - 1)
                    loadingText.text = `Ready ${loading - loadSeconds - 1}`;
                  loadSeconds++;
                  if (loadSeconds === loading) {
                    loadingText.destroy();
                    loadingBackdrop.destroy();
                    loadingLayer.destroy();
                    let {
                        width: { min: minWidth, max: maxWidth },
                        drops,
                        seconds,
                      } = values,
                      kinds = ["s", "m", "l"],
                      dropRanges = scene.spriteList
                        .map((sprite) => ({
                          x: sprite.x,
                          y: sprite.y + sprite.displayHeight / 2,
                        }))
                        .filter(
                          (val) =>
                            val.x > width * minWidth && val.x < width * maxWidth
                        );
                    scene.gameSeconds = seconds;
                    scene.dropRanges = dropRanges;
                    let randomRanges = MATH.RND.shuffle(dropRanges);
                    Array.from({ length: drops }).map((_, index) => {
                      let { x, y } = randomRanges[index],
                        drop = MATH.RND.between(0, 2);
                      dropList.push(
                        scene.add.sprite(x, y, `raindrop_${kinds[drop]}`)
                      );
                      return null;
                    });
                    let dropLayer = scene.add.layer();
                    dropLayer.add(dropList);
                    layerList.push(dropLayer);
                    scene.dropList = dropList;
                    scene.gameStart = Date.now();
                    let timeSprite = scene.add.sprite(0, 0, "time_left"),
                      timeLabel = scene.add
                        .text(25, -5, resolveTime(seconds), {
                          align: "center",
                        })
                        .setFontSize(14)
                        .setOrigin(0.5, 0.5),
                      timeContainer = scene.add.container(width / 2, 40, [
                        timeSprite,
                        timeLabel,
                      ]);
                    scene.time.addEvent({
                      callback: () => {
                        seconds -= 1;
                        scene.gameCurrentSeconds = seconds;
                        timeLabel.text = resolveTime(seconds);
                        if (seconds === 0) {
                          EventTracker.instance.trackEvent(
                            "Complete Raindrop Game",
                            {
                              Score: scene.score,
                              Time: scene.gameSeconds,
                            }
                          );
                          common.resolveAction(scene, {
                            action: "TRAVEL",
                            value: 1,
                          });
                          scene.gamePlayerObject.destroy();
                          scene.gamePlayerObject = null;
                          scene.scene.start("MAIN", {
                            step: scene.step,
                            data: scene.data,
                            sheet: scene.playerSheet,
                          });
                        }
                      },
                      repeat: seconds - 1,
                      delay: 1000,
                    });
                    layerObject.add(timeContainer);
                  }
                },
                repeat: loading,
                delay: 1000,
              });
              scene.rainDropGameScene = true;
            }
            break;
          default:
            console.log("Unknown game");
        }
      } else if (name === "STYLE") {
        let { size, colors = [] } = values,
          colorList = colors.map((item) => {
            let { color, position, sheet } = item,
              rectangle = scene.add.rectangle(
                position.x,
                position.y,
                size,
                size,
                color
              );
            rectangle = rectangle.setInteractive();
            rectangle.on("pointerdown", () => {
              if (scene.playerSheet !== sheet) {
                scene.playerSheet = sheet;
                spriteMap.player.setTexture(sheet, spriteMap.player.frame.name);
              }
            });
            return rectangle;
          }),
          colorbg = scene.add.sprite(0, 0, "colorbg"),
          styleContainer = scene.add.container(
            scene.camera.width / 2 + size * 6,
            scene.camera.height / 2 + size * 6,
            [colorbg, ...colorList]
          );
        spriteMap["style_container"] = styleContainer;
        styleContainer.setVisible(false);
      } else if (name === "RECOMMENDATIONS") {
        EventTracker.instance.trackEvent("Complete Game", {
          "Game-Name": scene.game_name,
        });
        (async () => {
          const { success, data } = await GameService.dynamic(fetchQuery, {});
          if (success && !scene.recommendationScreen) {
            let { items } = coordinates;
            common.addScrollContainer(
              scene,
              data,
              "RECOMMENDATIONS",
              {
                x: common.resolvePoint(items.x, width),
                y: common.resolvePoint(items.y, height),
              },
              { w: items.w, h: items.h }
            );
            scene.recommendationScreen = true;
          }
        })();
      } else if (name === "PAST") {
        if (layer.name === "PAST" && !scene.pastScreen) {
          scene.activities = scene.activities || { kind: "clouds" };
          scene.add.circle();
          let getData = (monthContainer, spriteMap) => {
            let selectedMonth = monthContainer.list.find((ctr) => ctr.selected),
              key = selectedMonth.list[1].text,
              cloudsBool = spriteMap.clouds_selected.visible,
              recommendedBool = spriteMap.recommended_selected.visible,
              childKey = cloudsBool
                ? "clouds"
                : recommendedBool
                ? "recommendations"
                : "",
              actualKey = Object.keys(data).find(
                (item) => data[item].text === key
              ),
              list = data[actualKey][childKey];
            return { list, childKey };
          };
          let data = {},
            { month, items } = coordinates;
          scene.recommendations.map((item) => {
            data[item.date] = {
              text: item.text,
              clouds: item.inputs,
              recommendations: item.list,
            };
            return null;
          });
          let monthContainer = common.addScrollContainer(
            scene,
            data,
            "PAST_MONTHLY",
            {
              x: common.resolvePoint(month.x, width),
              y: common.resolvePoint(month.y, height),
            },
            { w: month.w, h: month.h }
          );
          layerObject.add(monthContainer);
          let { list, childKey } = getData(monthContainer, spriteMap);
          let globalContainer = common.addScrollContainer(
            scene,
            list,
            childKey === "clouds" ? "PAST_CLOUDS" : "RECOMMENDATIONS",
            {
              x: common.resolvePoint(items.x, width),
              y: common.resolvePoint(items.y, height),
            },
            { w: items.w, h: items.h }
          );
          layerObject.add(globalContainer);
          monthContainer.list.map((container) => {
            container.on("pointerdown", () => {
              if (!container.selected) {
                let current = monthContainer.list.find((ctr) => ctr.selected);
                current.selected = false;
                current.list[0].fillColor = "0x0AB5CD";
                container.selected = true;
                container.list[0].fillColor = "0x016876";
                scene.activities.month = container.list[1].text;
                globalContainer.destroy();
                scene.pastScreen = false;
                scene.scene.restart();
              }
            });
            return null;
          });
          spriteMap.clouds.on("pointerdown", () => {
            globalContainer.destroy();
            scene.activities.kind = "clouds";
            scene.pastScreen = false;
            scene.scene.restart();
          });
          spriteMap.recommended.on("pointerdown", () => {
            globalContainer.destroy();
            scene.activities.kind = "recommended";
            scene.pastScreen = false;
            scene.scene.restart();
          });
          scene.pastScreen = true;
        }
      }
      return null;
    });
    if (spriteMap.sound && spriteMap.mute) {
      if (scene.bgm.isPaused) {
        spriteMap.sound.setVisible(false);
        spriteMap.mute.setVisible(true);
      } else if (scene.bgm.isPlaying) {
        spriteMap.sound.setVisible(true);
        spriteMap.mute.setVisible(false);
      }
    }
  }

  update(time, delta) {
    if (this.gameIntro) {
      let { pointer, player } = this.gameIntro,
        spritesheet = this.data.game.game.spritesheet.player,
        { width } = this.camera,
        stepDelta = delta / 4;
      if (pointer.x > width * 0.5) {
        if (!pointer.flipX) {
          pointer.x -= stepDelta;
          if (pointer.x < width * 0.5) pointer.setVisible(false);
        } else {
          pointer.setVisible(false);
          player.setFrame(spritesheet[`bucket0_fly`]);
          if (player.x < width * 0.8) player.x += stepDelta;
          else {
            player.setFrame(spritesheet[`bucket0_idle`]);
            this.gameIntro = null;
            this.scene.start("MAIN", {
              step: this.step + 1,
              data: this.data,
              sheet: this.playerSheet,
            });
          }
        }
      } else if (pointer.x < width * 0.5) {
        player.setFrame(spritesheet[`bucket0_fly`]);
        if (pointer.flipX) {
          pointer.x += stepDelta;
        } else {
          if (player.x > width * 0.2) player.x -= stepDelta;
          else {
            pointer.setFlipX(true);
            pointer.setVisible(true);
            pointer.x = width * 0.2;
            player.setFrame(spritesheet[`bucket0_idle`]);
            player.setFlipX(true);
          }
        }
      }
    }
    if (this.playerObject) {
      if (this.playerObject.x <= this.camera.width * 0.8)
        this.playerObject.x += delta / 4;
      else {
        this.playerAnimation.running += 1;
        this.playerObject.anims.play(
          this.playerAnimation.list[this.playerAnimation.running]
        );
        common.resolveAction(this, this.playerAnimation.end);
        this.scene.start("MAIN", {
          step: this.step,
          data: this.data,
          sheet: this.playerSheet,
        });
      }
    }
    if (
      this.dropList &&
      this.dropList.length > 0 &&
      this.gamePlayerObject &&
      this.rainDropGameScene
    ) {
      let { height } = this.camera,
        gameObject = this.gamePlayerObject,
        spritesheet = this.data.game.game.spritesheet.player,
        {
          height: { max: maxHeight },
          score,
          end,
        } = this.data.game.game.screens[this.step].values,
        { X1, X2, Y1, Y2 } = common.getBucketBox(this, gameObject),
        kinds = ["s", "m", "l"],
        ranges = this.dropRanges,
        reset = (item) => {
          let drop = MATH.RND.between(0, 2),
            randomRanges = MATH.RND.shuffle(ranges),
            { x, y } = randomRanges[0];
          item.setTexture(`raindrop_${kinds[drop]}`);
          item.x = x;
          item.y = y;
        };
      this.dropList.map((item) => {
        if (item.x >= X1 && item.x <= X2 && item.y >= Y1 && item.y <= Y2) {
          this.score += 1;
          let { x: x3, y: y3, flipX: flipped } = this.gamePlayerObject,
            scoreText = this.add
              .text(x3 + (flipped ? 1 : -1) * 40, y3 - 20, this.score, {
                fontFamily: "A-AlloyInk",
                color: "#0000ff",
              })
              .setFontSize(24)
              .setOrigin(0.5, 0.5);
          this.time.addEvent({
            callback: () => scoreText.destroy(),
            repeat: 1,
            delay: 200,
          });
          let frameKey = Object.keys(spritesheet).find(
            (key) => spritesheet[key] === gameObject.frame.name.toString()
          );
          if (frameKey) {
            let pastScore = parseInt((this.score - 1) / 3),
              newScore = parseInt(this.score / 3);
            gameObject.setFrame(
              spritesheet[
                frameKey.replace(new RegExp(pastScore.toString()), newScore)
              ]
            );
          }
          if (this.score >= score) {
            EventTracker.instance.trackEvent("Complete Raindrop Game", {
              Score: this.score,
              Time: this.gameCurrentSeconds,
            });
            common.resolveAction(this, end);
            if (this.gamePlayerObject) this.gamePlayerObject.destroy();
            this.gamePlayerObject = null;
            this.rainDropGameScene = null;
            this.dropList = null;
            this.scene.start("MAIN", {
              step: this.step,
              data: this.data,
              sheet: this.playerSheet,
            });
          }
          reset(item);
        } else {
          if (item.y < height * maxHeight) item.y += 2;
          else reset(item);
        }
        return null;
      });
    }
  }
}

export default MainScene;
